/**
 * useShadowLCRequirements Hook
 *
 * Fetches LC requirements derived from shadow spawn batches.
 * Groups requirements by strain and indicates LC availability.
 */

import { useState, useEffect, useMemo } from 'react'
import { supabase } from '@/supabase-client'

export function useShadowLCRequirements() {
  const [requirements, setRequirements] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchRequirements = async () => {
    setLoading(true)
    setError(null)

    try {
      // Get shadow batches that need LC
      const { data, error: fetchError } = await supabase
        .from('production_timeline_complete')
        .select('*')
        .eq('is_shadow', true)
        .order('inoculation_date', { ascending: true })

      if (fetchError) throw fetchError

      // Group shadow batches by strain and calculate LC requirements
      const grouped = data?.reduce((acc, batch) => {
        const strainId = batch.strain_id
        const spawnPhase = batch.batch_phases?.find(p =>
          p.phase_name === 'Spawn Production'
        )

        if (!acc[strainId]) {
          acc[strainId] = {
            strain_id: strainId,
            strain_code: batch.strain_code,
            strain_name: batch.strain_name,
            batches: [],
            total_blocks: 0,
            earliest_spawn_date: null,
            has_available_lc: false // Will be enriched
          }
        }

        acc[strainId].batches.push(batch)
        acc[strainId].total_blocks += batch.total_blocks || 0

        const spawnDate = spawnPhase?.planned_start || batch.inoculation_date
        if (!acc[strainId].earliest_spawn_date ||
            spawnDate < acc[strainId].earliest_spawn_date) {
          acc[strainId].earliest_spawn_date = spawnDate
        }

        return acc
      }, {}) || {}

      // Check which strains have available LC
      const { data: availableLCs } = await supabase
        .from('liquid_cultures')
        .select('strain_id')
        .in('status', ['ready', 'in_use'])

      const availableStrains = new Set(availableLCs?.map(lc => lc.strain_id) || [])

      // Enrich with LC availability
      const requirementsArray = Object.values(grouped).map(req => ({
        ...req,
        has_available_lc: availableStrains.has(req.strain_id),
        batch_count: req.batches.length
      }))

      // Sort by earliest spawn date
      requirementsArray.sort((a, b) => {
        if (!a.earliest_spawn_date) return 1
        if (!b.earliest_spawn_date) return -1
        return new Date(a.earliest_spawn_date) - new Date(b.earliest_spawn_date)
      })

      setRequirements(requirementsArray)
    } catch (err) {
      console.error('Error fetching LC requirements:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchRequirements()
  }, [])

  // Compute pending batch counts per strain for easy lookup
  const pendingBatchCounts = useMemo(() => {
    return requirements.reduce((acc, req) => {
      acc[req.strain_id] = req.batch_count
      return acc
    }, {})
  }, [requirements])

  // Strains missing LC
  const strainsMissingLC = useMemo(() => {
    return requirements.filter(r => !r.has_available_lc)
  }, [requirements])

  return {
    requirements,
    pendingBatchCounts,
    strainsMissingLC,
    loading,
    error,
    refetch: fetchRequirements
  }
}
